import { Capacitor } from "@capacitor/core";
import { AppleLogo, GooglePlayLogo, MapPin, BellRinging, ForkKnife } from "@phosphor-icons/react";
import { trackSiteEvent } from "../lib/analytics";
import { APP_STORE_URL, PLAY_STORE_URL, ANDROID_LIVE } from "../lib/appStores";

const BENEFITS = [
  { icon: MapPin, text: "The live truck map, wherever you are" },
  { icon: BellRinging, text: "Push alerts from trucks you follow" },
  { icon: ForkKnife, text: "Menus, hours & one-tap directions" },
];

// Strong homepage app section. Never renders inside the native app.
export default function AppPromoSection() {
  if (Capacitor.isNativePlatform()) return null;
  return (
    <section
      className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pb-20 sm:pb-28"
      data-testid="app-promo-section"
    >
      <div className="rounded-3xl border border-border bg-gradient-to-br from-primary/10 via-card to-card overflow-hidden">
        <div className="grid md:grid-cols-[1fr_auto] gap-8 items-center p-6 sm:p-10">
          <div>
            <div className="text-[11px] font-bold tracking-widest text-primary uppercase mb-2">
              Get the app
            </div>
            <h2 className="font-display font-bold tracking-tight text-3xl sm:text-4xl leading-tight">
              TruckTrackr in <span className="text-primary">your pocket.</span>
            </h2>
            <p className="mt-3 text-sm sm:text-base text-muted-foreground max-w-lg leading-relaxed">
              Find nearby trucks, follow your favorites, check schedules and
              menus, and get directions wherever you are.
            </p>
            <ul className="mt-5 space-y-2.5">
              {BENEFITS.map((b) => (
                <li key={b.text} className="flex items-center gap-2.5 text-sm">
                  <span className="h-8 w-8 rounded-xl bg-primary/15 text-primary grid place-items-center flex-none">
                    <b.icon weight="duotone" className="h-4 w-4" />
                  </span>
                  {b.text}
                </li>
              ))}
            </ul>
            <div className="mt-6 flex flex-wrap gap-3">
              <a
                href={APP_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackSiteEvent("cta_app_store")}
                className="inline-flex items-center gap-2.5 h-12 px-6 rounded-full bg-foreground text-background text-sm font-semibold hover:opacity-90 transition-opacity"
                data-testid="app-promo-appstore-cta"
              >
                <AppleLogo weight="fill" className="h-5 w-5" />
                App Store
              </a>
              {ANDROID_LIVE && (
                <a
                  href={PLAY_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackSiteEvent("cta_google_play")}
                  className="inline-flex items-center gap-2.5 h-12 px-6 rounded-full bg-foreground text-background text-sm font-semibold hover:opacity-90 transition-opacity"
                  data-testid="app-promo-play-cta"
                >
                  <GooglePlayLogo weight="fill" className="h-5 w-5" />
                  Google Play
                </a>
              )}
            </div>
            <p className="mt-2.5 text-xs text-muted-foreground">
              Free for iPhone{ANDROID_LIVE ? " and Android" : ""}.
            </p>
          </div>
          {/* Real app screenshot (live map) in a simple device frame. */}
          <div className="relative mx-auto md:mx-0 w-44 sm:w-56 flex-none">
            <div className="rounded-[2rem] sm:rounded-[2.4rem] border-[6px] border-foreground/90 bg-foreground/90 shadow-2xl overflow-hidden">
              <img
                src="/app-map-shot.jpg"
                alt="TruckTrackr iPhone app showing the live food truck map"
                loading="lazy"
                className="block w-full"
                data-testid="app-promo-screenshot"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
